import { useState, type ReactNode } from 'react'
import { Link, useParams } from 'react-router-dom'
import { BadgeDollarSign, CheckCircle2, ChevronDown, Loader2, ShieldCheck, UserX } from 'lucide-react'
import { GotALetterWizard } from '@/components/claim/GotALetterWizard'
import { LetterClaimInstallCta } from '@/components/claim/LetterClaimInstallCta'
import { MapsProvider } from '@/components/maps/MapsProvider'
import { useAuth } from '@/hooks/use-auth'
import { splitAddressDisplay } from '@/lib/address'
import biddleLogo from '@/assets/biddle-logo.png'

const FAQS = [
  {
    question: 'Can people see that I’ve registered?',
    answer:
      'No. Registration is private. Nobody can publicly see that you’ve registered your property — it simply means you’ll be notified if someone makes a Biddle (expresses interest).',
  },
  {
    question: 'Is registering an obligation to sell?',
    answer:
      "No. Registering simply lets a verified buyer privately express interest. You're never obligated to respond. Biddles are non-binding expressions of interest — using Biddle to connect or chat does not create a legal offer or contract. That only happens if you later choose to make a formal agreement with the other party.",
  },
  {
    question: 'Can I still use a real estate agent if I want to?',
    answer:
      "Yes. Being registered as the owner of your property on Biddle doesn't mean you have to sell through Biddle — it's simply an extra, non-exclusive channel through which you might receive interest in your property. You're free to list with an agent, sell privately, or do both.",
  },
  {
    question: 'Will buyers see my details?',
    answer:
      "No. A private in-app chat only opens if you choose to engage with a buyer's expression of interest.",
  },
  {
    question: 'How did you get my address?',
    answer:
      'We posted to "The Owner" at your property\'s public street address. We don\'t know your name, and no personal details have been shared.',
  },
  {
    question: 'Is Biddle really free for owners?',
    answer:
      "Yes. There are no fees for owners. Biddle doesn't act as an agent and doesn't take commissions.",
  },
] as const

/**
 * Full-page campaign landing for `/o` and `/o/:code`.
 * Emulates the biddle.io owner letter page (hero, trust, explainer, FAQ)
 * while embedding the real web claim wizard as the CTA.
 */
export function GotALetterPage() {
  const { code: routeCode } = useParams<{ code?: string }>()
  const { isLoading: authLoading } = useAuth()
  const [streetAddress, setStreetAddress] = useState('')
  const [claimedPropertyId, setClaimedPropertyId] = useState<string | null>(null)
  const [resolveFinished, setResolveFinished] = useState(!routeCode)

  // Wait for letter resolve + auth so the claim card doesn’t pop in after the hero.
  const waitingForLetter = Boolean(routeCode) && (!resolveFinished || authLoading)
  const claimDone = Boolean(claimedPropertyId)

  return (
    <MapsProvider>
    <div className="campaign-letter-page relative min-h-dvh overflow-x-hidden">
      <div className="campaign-letter-dots pointer-events-none absolute inset-0" aria-hidden />

      <header className="campaign-letter-glass sticky top-0 z-50 border-b border-white/20 py-2.5 px-4 sm:px-6">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-4">
          <Link to="/search" className="block" aria-label="Biddle home">
            <img src={biddleLogo} alt="Biddle" className="h-7 w-auto sm:h-9" />
          </Link>
          <span className="text-xs font-medium tracking-wide text-muted sm:text-sm">
            Register your property
          </span>
        </div>
      </header>

      {waitingForLetter ? (
        <section className="relative flex flex-col items-center justify-center px-5 py-24 text-center sm:px-6">
          <Loader2 className="mb-4 h-8 w-8 animate-spin text-biddle-blue" />
          <p className="text-muted">Loading your property…</p>
        </section>
      ) : null}

      {/* Keep one wizard instance mounted so resolve isn't replayed when the shell reveals. */}
      <div className={waitingForLetter ? 'hidden' : undefined}>
        <main className="relative">
          <HeroSection streetAddress={streetAddress} />

          <section className="px-4 pb-8 pt-0 sm:px-6 sm:pb-12 sm:pt-2">
            <div className="mx-auto max-w-2xl">
              <div className="rounded-2xl border border-white/60 bg-white/85 p-4 shadow-lg backdrop-blur-sm sm:rounded-3xl sm:p-8">
                {claimDone && claimedPropertyId ? (
                  <div className="text-center">
                    <div className="mb-4 flex justify-center">
                      <CheckCircle2 className="h-12 w-12 text-success" strokeWidth={1.75} />
                    </div>
                    <h2 className="font-campaign-display text-xl font-bold text-navy sm:text-3xl">
                      Your property has been added to your account
                    </h2>
                    <Link
                      to={`/properties/${claimedPropertyId}/biddles`}
                      className="mt-5 inline-flex h-11 items-center justify-center rounded-xl bg-biddle-blue px-5 text-sm font-semibold text-white transition hover:bg-biddle-blue-dark"
                    >
                      View my property&apos;s Biddles
                    </Link>
                    <div className="mx-auto mt-5 max-w-sm">
                      <LetterClaimInstallCta />
                    </div>
                  </div>
                ) : (
                  <>
                    {!routeCode ? (
                      <>
                        <h2 className="font-campaign-display mb-1 text-center text-xl font-bold text-navy sm:mb-2 sm:text-3xl">
                          Got a letter?
                        </h2>
                        <p className="mb-3 text-center text-xs leading-snug text-muted sm:mb-5 sm:text-base sm:leading-relaxed">
                          Enter the code from your letter to register your property.
                        </p>
                      </>
                    ) : null}
                    <GotALetterWizard
                      initialCode={routeCode}
                      syncUrl
                      hideIntro
                      onPropertyAddress={(full) =>
                        setStreetAddress(splitAddressDisplay(full).street)
                      }
                      onResolveFinished={() => setResolveFinished(true)}
                      onComplete={(propertyId) => setClaimedPropertyId(propertyId)}
                    />
                  </>
                )}
              </div>
            </div>
          </section>

          <ExplainerSection />
          <FaqSection />
        </main>

        <footer className="border-t border-border bg-white px-5 py-10 sm:px-6">
          <div className="mx-auto max-w-3xl text-center">
            <div className="mb-6 flex justify-center">
              <img src={biddleLogo} alt="Biddle" className="h-8 w-auto opacity-60" />
            </div>
            <div className="mb-6 flex flex-wrap justify-center gap-6 text-sm text-muted">
              <a
                href="https://biddle.io/privacy"
                className="transition-colors hover:text-biddle-blue"
                target="_blank"
                rel="noopener noreferrer"
              >
                Privacy
              </a>
              <a
                href="https://biddle.io/terms"
                className="transition-colors hover:text-biddle-blue"
                target="_blank"
                rel="noopener noreferrer"
              >
                Terms
              </a>
              <a
                href="https://biddle.io/support"
                className="transition-colors hover:text-biddle-blue"
                target="_blank"
                rel="noopener noreferrer"
              >
                Support
              </a>
            </div>
            <p className="mb-2 text-xs text-muted">
              If you are not the owner of this property, please discard this letter and disregard the
              QR code.
            </p>
            <p className="text-xs text-muted">
              © {new Date().getFullYear()} Biddle Technologies Pty Ltd. All rights reserved.
            </p>
          </div>
        </footer>
      </div>
    </div>
    </MapsProvider>
  )
}

function HeroSection({ streetAddress }: { streetAddress: string }) {
  return (
    <section className="px-6 pb-3 pt-8 sm:px-8 sm:pb-8 sm:pt-12">
      <div className="mx-auto max-w-5xl">
        <div className="flex flex-col items-center gap-3 sm:gap-6 lg:flex-row lg:items-center lg:gap-12">
          <div className="flex flex-shrink-0 justify-center">
            <img
              src="/billy-envelope.png"
              alt="Billy the Biddle mascot"
              className="campaign-letter-float h-auto w-36 sm:w-48 lg:w-44"
              draggable={false}
            />
          </div>

          <div className="flex-1 px-1 text-center sm:px-0 lg:text-left">
            <h1 className="font-campaign-display mb-4 text-[1.65rem] font-extrabold leading-[1.2] tracking-tight text-navy sm:mb-6 sm:text-4xl lg:text-5xl">
              Register as the owner{' '}
              <span className="whitespace-nowrap">of your property</span>
            </h1>

            <p className="mx-auto mb-4 max-w-2xl text-sm leading-snug text-muted sm:mb-7 sm:text-lg sm:leading-relaxed lg:mx-0 lg:text-xl">
              {streetAddress ? (
                <>
                  Get notified of every Biddle on{' '}
                  <span className="font-semibold text-navy">{streetAddress}</span> — privately, with
                  no obligation to sell, and no fees.
                </>
              ) : (
                <>
                  Get notified of every Biddle on your home — privately, with no obligation to sell,
                  and no fees.
                </>
              )}
            </p>

            <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1.5 sm:gap-x-6 sm:gap-y-3 lg:justify-start">
              <TrustPill icon={<UserX className="h-3.5 w-3.5 sm:h-4 sm:w-4" />} text="No agents" />
              <TrustPill
                icon={<ShieldCheck className="h-3.5 w-3.5 sm:h-4 sm:w-4" />}
                text="Identity-verified buyers"
              />
              <TrustPill
                icon={<BadgeDollarSign className="h-3.5 w-3.5 sm:h-4 sm:w-4" />}
                text="100% Free"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

function TrustPill({ icon, text }: { icon: ReactNode; text: string }) {
  return (
    <div className="flex items-center gap-1.5 text-muted sm:gap-2">
      <span className="text-biddle-blue">{icon}</span>
      <span className="text-xs font-medium sm:text-sm">{text}</span>
    </div>
  )
}

function ExplainerSection() {
  return (
    <section className="bg-white/70 px-5 py-12 sm:px-6 sm:py-16">
      <div className="mx-auto max-w-2xl">
        <h2 className="font-campaign-display mb-10 text-center text-2xl font-bold text-navy sm:text-3xl">
          What is Biddle?
        </h2>
        <div className="space-y-8 text-base leading-relaxed text-charcoal/90 sm:space-y-9 sm:text-lg">
          <p>
            Biddle is an Australian platform that lets identity-verified buyers register non-binding
            expressions of interest (Biddles) directly with property owners. Owners who register their
            property can review Biddles and chat privately with the interested party, or simply ignore
            them if they wish. Biddle does not act as an agent or take commissions.
          </p>
          <p>
            Registering is private — nobody can publicly see that you’ve registered. It simply means
            you’ll be notified if someone makes a Biddle (expresses interest).
          </p>
          <p>
            Questions? Email{' '}
            <a href="mailto:support@biddle.io" className="font-medium text-biddle-blue hover:underline">
              support@biddle.io
            </a>{' '}
            or visit{' '}
            <a
              href="https://biddle.io"
              className="font-medium text-biddle-blue hover:underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              biddle.io
            </a>
            .
          </p>
        </div>
      </div>
    </section>
  )
}

function FaqSection() {
  return (
    <section className="px-5 py-12 sm:px-6 sm:py-16">
      <div className="mx-auto max-w-2xl">
        <h2 className="font-campaign-display mb-8 text-center text-2xl font-bold text-navy sm:text-3xl">
          Frequently asked questions
        </h2>
        <div className="space-y-3">
          {FAQS.map((faq) => (
            <details
              key={faq.question}
              className="group rounded-xl border border-white/50 bg-white/70 px-6 shadow-sm backdrop-blur-sm"
            >
              <summary className="flex cursor-pointer list-none items-center justify-between gap-3 py-5 text-left font-medium text-navy marker:content-none [&::-webkit-details-marker]:hidden">
                <span>{faq.question}</span>
                <ChevronDown className="h-4 w-4 shrink-0 text-muted transition group-open:rotate-180" />
              </summary>
              <p className="pb-5 text-sm leading-relaxed text-muted sm:text-base">{faq.answer}</p>
            </details>
          ))}
        </div>
      </div>
    </section>
  )
}
